import React from 'react';
import { useQuery } from 'react-query';
import * as consts from 'config/consts';
import { getAssetHistory } from 'config/api';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import { AssetTypeKey } from 'components/features/utils/assetType';
import { AssetHistoryItemsProps } from 'components/features/home/assetStuffing/AssetStuffing';
import Loading from 'components/commons/loading/Loading';
import { PositionList } from './PositionList';

export const sortByNumber = (
  first: number | null,
  second: number | null
): number => {
  const firstNum = first || 0;
  const secondNum = second || 0;
  if (firstNum > secondNum) {
    return -1;
  } else if (firstNum < secondNum) {
    return 1;
  } else {
    return 0;
  }
};

const positionDispList: AssetTypeKey[] = [
  consts.AssetType.国内現物,
  consts.AssetType.未上場株式,
  consts.AssetType.投資信託,
  consts.AssetType.外国株式,
  consts.AssetType['国内ETF・ETN'],
  consts.AssetType['外国ETF・ETN'],
  consts.AssetType.国内REIT,
  consts.AssetType.外国REIT,
  consts.AssetType.国内債券,
  consts.AssetType.外国債券,
  consts.AssetType.国内EB,
  consts.AssetType.外国EB,
  consts.AssetType.不動産,
  consts.AssetType.ロボアドバイザー,
  consts.AssetType.信用,
  consts.AssetType.預金_MRF,
  consts.AssetType.その他,
];

export const AssetListInternal = ({
  positions,
  assetsHistory,
  onTradeItemClick,
}: {
  positions: PositionItemsProps[];
  assetsHistory: AssetHistoryItemsProps[];
  onTradeItemClick: (symbol: string) => void;
}): React.ReactElement => {
  return (
    <>
      <div className="bg-white">
        <div className="pl-4 py-1 text-sm font-bold text-bluegray-550 border-t">
          内訳
        </div>
        {positionDispList.map((assetTypekey, i) => {
          return (
            <React.Fragment key={i}>
              <PositionList
                {...{
                  assetType: assetTypekey,
                  positions,
                  assetsHistory,
                  onTradeItemClick,
                }}
              />
            </React.Fragment>
          );
        })}
        <div className="border-t" />
        <div className="px-4 py-8 text-xs text-bluegray-500">
          ※外国株式の評価額は前営業日の終値に本日9:00（日本時間）時点の為替レートを掛けて算出しています。
          <br />
          ※一部の投資信託は連携後、1〜2営業日ほどデータ反映が遅れる可能性があります。
          <br />
          ※更新タイミングによっては当日の取り込みができない場合があります。その場合は前日比が正しく表示されない場合があります。
        </div>
      </div>
    </>
  );
};

const AssetListLoading = () => {
  return (
    <>
      <div className="pl-4 py-1 text-sm font-bold text-bluegray-550 border-t border-b">
        内訳
      </div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{ paddingTop: '60px', paddingBottom: '60px' }}
      />
    </>
  );
};

export const AssetList = ({
  positions,
  onTradeItemClick,
}: {
  positions: PositionItemsProps[];
  onTradeItemClick: (symbol: string) => void;
}): React.ReactElement => {
  const { data: assetsHistory_ } = useQuery(
    'dailyAssetHistory',
    getAssetHistoryAPI,
    {
      refetchOnWindowFocus: false,
    }
  );

  if (assetsHistory_ === null || assetsHistory_ === undefined)
    return <AssetListLoading />;

  const assetsHistory = assetsHistory_ as AssetHistoryItemsProps[];

  return (
    <AssetListInternal {...{ positions, assetsHistory, onTradeItemClick }} />
  );
};

const getAssetHistoryAPI = async () => {
  const resAssetHistory_ = await getAssetHistory(
    undefined,
    undefined,
    undefined,
    undefined,
    consts.reportType.日次
  );

  // memo: 不要な項目は引き継がない
  const resAssetHistory = resAssetHistory_.Items.map(item => {
    return {
      day: item.Day,
      totalAssetAmount: item.TotalAssetAmount,
      stockAmount: item.StockAmount,
      marginAmount: item.MarginAmount,
      marginProfitOrLoss: item.MarginProfitOrLoss,
      roboAdvisorAmount: item.RoboAdvisorAmount,
      uSStockAmount: item.USStockAmount,
      investmentTrustAmount: item.InvestmentTrustAmount,
      unlistedStockAmount: item.UnlistedStockAmount,
      etfEtnAmount: item.EtfEtnAmount,
      foreignEtfEtnAmount: item.ForeignEtfEtnAmount,
      reitAmount: item.ReitAmount,
      foreignReitAmount: item.ForeignReitAmount,
      bondAmount: item.BondAmount,
      foreignBondAmount: item.ForeignBondAmount,
      ebAmount: item.EbAmount,
      foreignEbAmount: item.ForeignEbAmount,
      realEstateAmount: item.RealEstateAmount,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
    };
  });
  return resAssetHistory;
};
